import { TYRE_STATUSES, type TyreListQuery, type TyreView } from '@chukua/common';
import { Tag, type TableColumnsType } from 'antd';
import { Link } from 'react-router';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { formatDepth, formatKm, TYRE_STATUS_COLORS, TYRE_STATUS_LABELS } from '../policies/tyre-labels';
import { tyrePath } from '../tyres.paths';

export function fitmentLabel(tyre: TyreView): string {
  return tyre.fitment ? `${tyre.fitment.vehicleRegistration ?? 'Vehicle'} · ${tyre.fitment.positionCode}` : '—';
}

/** Each column's `key` is its sort field or filter param in `tyreListQuerySchema`. */
interface TyreColumnsOptions {
  list: Pick<ListQueryControls<TyreListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  /** Catalogue names the Brand and Size filters offer. */
  brands: string[];
  sizes: string[];
}

export function buildTyreColumns({ list, brands, sizes }: TyreColumnsOptions): TableColumnsType<TyreView> {
  return [
    {
      title: 'Serial',
      dataIndex: 'serial',
      key: 'serial',
      sorter: true,
      sortOrder: list.sortOrderFor('serial'),
      render: (serial: string, tyre) => (
        <Link to={tyrePath(tyre.id)} className="font-mono">
          {serial}
        </Link>
      ),
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      sorter: true,
      sortOrder: list.sortOrderFor('brand'),
      filters: brands.map((brand) => ({ text: brand, value: brand })),
      filteredValue: list.filteredValueFor('brand'),
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      sorter: true,
      sortOrder: list.sortOrderFor('size'),
      filters: sizes.map((size) => ({ text: size, value: size })),
      filteredValue: list.filteredValueFor('size'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      sortOrder: list.sortOrderFor('status'),
      filters: TYRE_STATUSES.map((status) => ({ text: TYRE_STATUS_LABELS[status], value: status })),
      filteredValue: list.filteredValueFor('status'),
      render: (status: TyreView['status']) => <Tag color={TYRE_STATUS_COLORS[status]}>{TYRE_STATUS_LABELS[status]}</Tag>,
    },
    { title: 'Fitted on', key: 'fitment', render: (_, tyre) => fitmentLabel(tyre) },
    {
      title: 'Tread',
      dataIndex: 'currentDepthMm',
      key: 'currentDepthMm',
      sorter: true,
      sortOrder: list.sortOrderFor('currentDepthMm'),
      render: formatDepth,
    },
    { title: 'Run', dataIndex: 'kmOnTyre', key: 'kmOnTyre', render: formatKm },
    { title: 'Purchased', dataIndex: 'purchasedOn', key: 'purchasedOn', sorter: true, sortOrder: list.sortOrderFor('purchasedOn') },
  ];
}
